// Bar today: summary cards, tickets with void (managers), top sellers & margin.
import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw, Loader2, Printer } from 'lucide-react';
import { printReceipt } from './receipt';
import { apiCall } from '../_shared';
import { CAT_LABEL, money } from './BarTab';
import { StaffShifts, staffShiftRows } from './StaffShifts';
import { CashCount } from './CashCount';

const Kpi = ({ label, value, sub, testId }) => (
  <div className="bg-white border rounded-xl p-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-xl font-bold text-slate-900">{value}</p>
    {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
  </div>
);

const BarDay = ({ canManage, onChanged, labels = CAT_LABEL }) => {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([apiCall(`/api/admin/bar/tickets?day=${day}`), apiCall(`/api/admin/bar/summary?day=${day}`)]);
      setData(t); setSum(s);
    } catch (e) { setMsg(e.message); }
  }, [day]);
  useEffect(() => { load(); }, [load]);
  const voidTicket = async (t) => {
    const reason = window.prompt(`Void ticket #${t.number} (${money(t.total)})? Stock returns and the ${t.payment === 'room' ? 'room charge is removed' : 'invoice is marked void'}. Reason:`);
    if (!reason) return;
    setBusy(t.id); setMsg('');
    try { await apiCall(`/api/admin/bar/tickets/${t.id}/void`, { method: 'POST', body: JSON.stringify({ reason }) }); setMsg(`Ticket #${t.number} voided.`); load(); onChanged?.(); }
    catch (e) { setMsg(e.message); } finally { setBusy(''); }
  };
  if (!data || !sum) return <p className="text-sm text-slate-500">Loading…</p>;
  const ticketList = data.tickets;
  const topList = sum.top;
  const byCat = sum.by_category;
  const byPay = sum.by_payment;
  const itemsOf = (t) => { const ls = t.lines; return ls.map((l) => `${l.qty}× ${l.name}`).join(', '); };
  const rows = ticketList.map((t) => (
    <tr key={t.id} className={`border-t ${t.status === 'void' ? 'opacity-50 line-through' : ''}`} data-testid={`bar-ticket-${t.id}`}>
      <td className="py-1.5 pl-2">#{t.number}</td>
      <td className="py-1.5 text-slate-500">{String(t.at).slice(11, 16)}</td>
      <td className="py-1.5">{itemsOf(t)}</td>
      <td className="py-1.5 capitalize">{t.payment === 'inroom' ? 'QR order' : t.payment}{t.spot ? ` · ${t.spot}` : ''}{t.room ? ` · ${t.room}${t.guest_name ? ` (${t.guest_name})` : ''}` : ''}</td>
      <td className="py-1.5 text-slate-500">{t.invoice_number || '—'}{t.fiscal_status === 'fiscalized' ? ' ✓' : ''}</td>
      <td className="py-1.5 text-right font-semibold">{money(t.total)}</td>
      <td className="py-1.5 text-right pr-2 whitespace-nowrap">
        <button type="button" onClick={() => printReceipt(t.id).catch((e) => setMsg(e.message))} className="inline-flex items-center gap-1 border rounded px-2 py-0.5 text-[11px] hover:bg-slate-50 mr-1" data-testid={`bar-receipt-${t.id}`}><Printer className="w-3 h-3" /> Receipt</button>
        {canManage && t.status === 'paid' && t.day === new Date().toISOString().slice(0, 10) && (
        <button type="button" onClick={() => voidTicket(t)} disabled={busy === t.id} className="inline-flex items-center gap-1 border rounded px-2 py-0.5 text-[11px] hover:bg-red-50 hover:text-red-700" data-testid={`bar-void-${t.id}`}>{busy === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Void</button>
      )}{t.status === 'void' && <span className="text-[11px] text-red-600 ml-1">void · {t.void_reason}</span>}{t.status === 'comp' && <span className="text-[11px] text-violet-700 ml-1" data-testid={`bar-ticket-comp-${t.id}`}>complimentary · {money(t.comp_value)} list</span>}</td>
    </tr>
  ));
  const top = topList.slice(0, 8).map((s) => <li key={s.product_id} className="flex justify-between gap-2" data-testid={`bar-top-${s.product_id}`}><span>{s.qty}× {s.name}</span><span className="text-slate-600">{money(s.revenue)}{s.margin_pct != null ? ` · ${s.margin_pct}%` : ''}</span></li>);
  const cats = Object.entries(byCat).map(([c, v]) => <li key={c} className="flex justify-between"><span>{labels[c] || c}</span><span>{money(v)}</span></li>);
  const pays = Object.entries(byPay).map(([p, v]) => `${p === 'inroom' ? 'QR orders' : p} ${money(v)}`).join(' · ') + (sum.comp_tickets ? ` · complimentary ${sum.comp_tickets}× (${money(sum.comp_value)} list value)` : '');
  return (
    <div className="space-y-3" data-testid="bar-day">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="border rounded px-2 py-1 text-sm" data-testid="bar-day-date" />
        {msg && <span className="text-xs text-slate-600" data-testid="bar-day-msg">{msg}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Kpi label="Revenue" value={money(sum.revenue)} sub={pays || '—'} testId="bar-kpi-revenue" />
        <Kpi label="Tickets" value={sum.tickets} sub={sum.void_count ? `${sum.void_count} void` : ''} testId="bar-kpi-tickets" />
        <Kpi label="Gross margin" value={sum.margin_pct != null ? `${sum.margin_pct}%` : '—'} sub={`COGS ${money(sum.cogs)}`} testId="bar-kpi-margin" />
        <Kpi label="Stock value" value={money(sum.stock_value)} sub={`${sum.low_stock.length} below threshold`} testId="bar-kpi-stock" />
        <Kpi label="Waste (30 d)" value={`${sum.waste_pct_30d}%`} sub="of portions moved" testId="bar-kpi-waste" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div className="bg-white border rounded-xl p-3"><p className="font-semibold text-slate-900 mb-1">Top sellers · margin</p>{top.length ? <ul className="space-y-0.5">{top}</ul> : <p className="text-slate-400">No sales yet.</p>}</div>
        <div className="bg-white border rounded-xl p-3"><p className="font-semibold text-slate-900 mb-1">By category</p>{cats.length ? <ul className="space-y-0.5">{cats}</ul> : <p className="text-slate-400">—</p>}</div>
      </div>
      <StaffShifts tickets={ticketList} />
      <CashCount day={day} canManage={canManage} staffOptions={[...new Set(staffShiftRows(ticketList).map((r) => r.staff).filter((x) => x !== 'guest' && x !== '—'))]} />
      <div className="overflow-x-auto bg-white border rounded-xl">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-500"><th className="py-2 pl-2">#</th><th>Time</th><th>Items</th><th>Payment</th><th>Invoice</th><th className="text-right">Total</th><th className="text-right pr-2"></th></tr></thead>
          <tbody>{rows.length ? rows : <tr><td colSpan={7} className="py-4 text-center text-slate-400">No tickets on this day.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
};

export default BarDay;
